import {
  CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  type CanonicalIndividualComplianceRequirementKey,
} from "./canonicalIndividualCompliance";

export const CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS =
  CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS;

export type CanonicalInternalComplianceReviewRequirementKey =
  CanonicalIndividualComplianceRequirementKey;

export const CANONICAL_INTERNAL_COMPLIANCE_REVIEW_DECISIONS = Object.freeze([
  "approved",
  "rejected",
  "changes_requested",
] as const);

export type CanonicalInternalComplianceReviewDecision =
  typeof CANONICAL_INTERNAL_COMPLIANCE_REVIEW_DECISIONS[number];

export const CANONICAL_INTERNAL_COMPLIANCE_REVIEW_STATES = Object.freeze([
  "pending",
  ...CANONICAL_INTERNAL_COMPLIANCE_REVIEW_DECISIONS,
] as const);

export type CanonicalInternalComplianceReviewState =
  typeof CANONICAL_INTERNAL_COMPLIANCE_REVIEW_STATES[number];

export const CANONICAL_INTERNAL_COMPLIANCE_REVIEWER_ROLE_SNAPSHOTS =
  Object.freeze([
    "super_admin",
    "admin",
    "ops_manager",
    "owner",
    "custom",
  ] as const);

export type CanonicalInternalComplianceReviewerRoleSnapshot =
  typeof CANONICAL_INTERNAL_COMPLIANCE_REVIEWER_ROLE_SNAPSHOTS[number];

export const CANONICAL_INTERNAL_COMPLIANCE_REASON_CODE_PATTERN =
  /^[a-z][a-z0-9_]{0,63}$/;
export const CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH = 2000;
export const CANONICAL_INTERNAL_COMPLIANCE_IDEMPOTENCY_KEY_PATTERN =
  /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
export const CANONICAL_INTERNAL_COMPLIANCE_QUEUE_LIMIT_MIN = 1;
export const CANONICAL_INTERNAL_COMPLIANCE_QUEUE_LIMIT_MAX = 100;
export const CANONICAL_INTERNAL_COMPLIANCE_QUEUE_LIMIT_DEFAULT = 25;
export const CANONICAL_INTERNAL_COMPLIANCE_CURSOR_MAX_LENGTH = 512;
export const CANONICAL_INTERNAL_COMPLIANCE_PREVIEW_TTL_MIN = 30;
export const CANONICAL_INTERNAL_COMPLIANCE_PREVIEW_TTL_MAX = 600;
export const CANONICAL_INTERNAL_COMPLIANCE_PREVIEW_TTL_DEFAULT = 300;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+$/;
const UNSAFE_NOTE_PATTERN =
  /<[^>]+>|https?:\/\/|authorization\s*:|bearer\s+|service[_ -]?role|refresh[_ -]?token|api[_ -]?key|uploads\/[0-9a-f-]{36}/i;

export type CanonicalInternalReviewDocumentSource =
  | Readonly<{
      source: "canonical";
      evidenceId: string;
      canPreview: true;
    }>
  | Readonly<{
      source: "none";
      canPreview: false;
    }>;

export type CanonicalInternalComplianceReviewQueueItem = Readonly<{
  organizationId: string;
  contractorId: string | null;
  targetPersonUserId: string | null;
  personDisplayName: string | null;
  requirementKey: CanonicalInternalComplianceReviewRequirementKey;
  required: boolean;
  sortOrder: number;
  source: CanonicalInternalReviewDocumentSource;
  fileName: string | null;
  mimeType: string | null;
  byteSize: number | null;
  submittedAt: string | null;
  expiresAt: string | null;
  evidenceEligible: boolean;
  reviewState: CanonicalInternalComplianceReviewState;
  latestDecisionId: string | null;
  operationallyVerified: boolean;
  failureReason: string | null;
}>;

export type CanonicalInternalComplianceReviewQueue = Readonly<{
  items: readonly CanonicalInternalComplianceReviewQueueItem[];
  nextCursor: string | null;
}>;

export type CanonicalInternalComplianceReviewDetail = Readonly<{
  organizationId: string;
  contractorId: string | null;
  targetPersonUserId: string;
  requirements: readonly CanonicalInternalComplianceReviewQueueItem[];
}>;

export type CanonicalInternalComplianceReviewHistoryItem = Readonly<{
  decisionId: string;
  evidenceId: string;
  decision: CanonicalInternalComplianceReviewDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerDisplayName: string | null;
  reviewerRole: CanonicalInternalComplianceReviewerRoleSnapshot;
  reviewerScopeId: string | null;
  previousDecisionId: string | null;
  decidedAt: string;
}>;

export type CanonicalInternalComplianceReviewHistory = Readonly<{
  items: readonly CanonicalInternalComplianceReviewHistoryItem[];
}>;

export type CanonicalInternalComplianceReviewDecisionResult = Readonly<{
  decisionId: string;
  evidenceId: string;
  decision: CanonicalInternalComplianceReviewDecision;
  reasonCode: string | null;
  note: string | null;
  reviewerRole: CanonicalInternalComplianceReviewerRoleSnapshot;
  reviewerScopeId: string | null;
  previousDecisionId: string | null;
  decidedAt: string;
  reviewState: CanonicalInternalComplianceReviewDecision;
  operationallyVerified: boolean;
}>;

const VALID_CANONICAL_INTERNAL_COMPLIANCE_REVIEW_INVENTORIES = Object.freeze([
  HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS,
] as const);

export type CanonicalInternalComplianceReviewPreview = Readonly<{
  signedUrl: string;
  expiresAt: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
}>;

export class CanonicalInternalComplianceReviewContractError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "CanonicalInternalComplianceReviewContractError";
  }
}

function invalid(code = "canonical_internal_compliance_review_invalid_response"): never {
  throw new CanonicalInternalComplianceReviewContractError(code);
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid();
  return value as Record<string, unknown>;
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const allowedSet = new Set(allowed);
  return Object.keys(value).every((key) => allowedSet.has(key));
}

function requireExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): void {
  if (
    !hasOnlyKeys(value, keys)
    || keys.some((key) => !(key in value))
  ) {
    invalid();
  }
}

function requireString(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) invalid();
  return value.trim();
}

function nullableString(value: unknown): string | null {
  return value === null ? null : requireString(value);
}

function requireUuid(value: unknown): string {
  if (typeof value !== "string" || !UUID_PATTERN.test(value)) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_uuid_invalid",
    );
  }
  return value;
}

function nullableUuid(value: unknown): string | null {
  return value === null ? null : requireUuid(value);
}

function requireBoolean(value: unknown): boolean {
  if (typeof value !== "boolean") invalid();
  return value;
}

function nullablePositiveNumber(value: unknown): number | null {
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    invalid();
  }
  return value;
}

function requireNonNegativeInteger(value: unknown): number {
  if (
    typeof value !== "number"
    || !Number.isFinite(value)
    || !Number.isInteger(value)
    || value < 0
  ) {
    invalid();
  }
  return value;
}

export function isCanonicalInternalComplianceReviewRequirementKey(
  value: unknown,
): value is CanonicalInternalComplianceReviewRequirementKey {
  return typeof value === "string"
    && CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS.includes(
      value as CanonicalInternalComplianceReviewRequirementKey,
    );
}

export function isCanonicalInternalComplianceReviewDecision(
  value: unknown,
): value is CanonicalInternalComplianceReviewDecision {
  return typeof value === "string"
    && CANONICAL_INTERNAL_COMPLIANCE_REVIEW_DECISIONS.includes(
      value as CanonicalInternalComplianceReviewDecision,
    );
}

export function isCanonicalInternalComplianceReviewState(
  value: unknown,
): value is CanonicalInternalComplianceReviewState {
  return typeof value === "string"
    && CANONICAL_INTERNAL_COMPLIANCE_REVIEW_STATES.includes(
      value as CanonicalInternalComplianceReviewState,
    );
}

export function isCanonicalInternalComplianceReviewerRoleSnapshot(
  value: unknown,
): value is CanonicalInternalComplianceReviewerRoleSnapshot {
  return typeof value === "string"
    && CANONICAL_INTERNAL_COMPLIANCE_REVIEWER_ROLE_SNAPSHOTS.includes(
      value as CanonicalInternalComplianceReviewerRoleSnapshot,
    );
}

export function assertCanonicalInternalComplianceReviewUuid(
  value: unknown,
): string {
  return requireUuid(value);
}

export function assertCanonicalInternalComplianceReviewCursor(
  value: unknown,
): string {
  if (
    typeof value !== "string"
    || value.length === 0
    || value.length > CANONICAL_INTERNAL_COMPLIANCE_CURSOR_MAX_LENGTH
    || !CURSOR_PATTERN.test(value)
  ) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_cursor_invalid",
    );
  }
  return value;
}

export function assertCanonicalInternalComplianceReviewQueueLimit(
  value: unknown,
): number {
  if (
    typeof value !== "number"
    || !Number.isInteger(value)
    || value < CANONICAL_INTERNAL_COMPLIANCE_QUEUE_LIMIT_MIN
    || value > CANONICAL_INTERNAL_COMPLIANCE_QUEUE_LIMIT_MAX
  ) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_limit_invalid",
    );
  }
  return value;
}

export function assertCanonicalInternalComplianceReviewPreviewTtl(
  value: unknown,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_preview_ttl_invalid",
    );
  }
  return value;
}

export function validateCanonicalInternalComplianceDecisionInput(args: {
  decision: unknown;
  reasonCode?: unknown;
  note?: unknown;
}): Readonly<{
  decision: CanonicalInternalComplianceReviewDecision;
  reasonCode: string | null;
  note: string | null;
}> {
  if (!isCanonicalInternalComplianceReviewDecision(args.decision)) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_decision_unsupported",
    );
  }
  const reasonCode =
    typeof args.reasonCode === "string" && args.reasonCode.trim()
      ? args.reasonCode.trim()
      : null;
  if (
    (args.decision === "approved" && reasonCode !== null)
    || (
      args.decision !== "approved"
      && (
        reasonCode === null
        || !CANONICAL_INTERNAL_COMPLIANCE_REASON_CODE_PATTERN.test(reasonCode)
      )
    )
  ) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_reason_invalid",
    );
  }
  const note =
    typeof args.note === "string" && args.note.trim() ? args.note.trim() : null;
  if (
    note !== null
    && (
      note.length > CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH
      || UNSAFE_NOTE_PATTERN.test(note)
    )
  ) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_note_invalid",
    );
  }
  return Object.freeze({ decision: args.decision, reasonCode, note });
}

export function parseCanonicalInternalReviewDocumentSource(
  value: unknown,
): CanonicalInternalReviewDocumentSource {
  const source = asRecord(value);
  if (
    source.source === "canonical"
    && source.canPreview === true
    && hasOnlyKeys(source, ["source", "evidenceId", "canPreview"])
  ) {
    return Object.freeze({
      source: "canonical",
      evidenceId: requireUuid(source.evidenceId),
      canPreview: true,
    });
  }
  if (
    source.source === "none"
    && source.canPreview === false
    && hasOnlyKeys(source, ["source", "canPreview"])
  ) {
    return Object.freeze({ source: "none", canPreview: false });
  }
  throw new CanonicalInternalComplianceReviewContractError(
    "canonical_internal_compliance_review_source_invalid",
  );
}

const QUEUE_ITEM_KEYS = [
  "organizationId",
  "contractorId",
  "targetPersonUserId",
  "personDisplayName",
  "requirementKey",
  "required",
  "sortOrder",
  "source",
  "fileName",
  "mimeType",
  "byteSize",
  "submittedAt",
  "expiresAt",
  "evidenceEligible",
  "reviewState",
  "latestDecisionId",
  "operationallyVerified",
  "failureReason",
] as const;

export function parseCanonicalInternalComplianceReviewQueueItem(
  value: unknown,
): CanonicalInternalComplianceReviewQueueItem {
  const row = asRecord(value);
  requireExactKeys(row, QUEUE_ITEM_KEYS);
  if (!isCanonicalInternalComplianceReviewRequirementKey(row.requirementKey)) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_requirement_unsupported",
    );
  }
  if (!isCanonicalInternalComplianceReviewState(row.reviewState)) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_state_unsupported",
    );
  }
  const source = parseCanonicalInternalReviewDocumentSource(row.source);
  const fileName = nullableString(row.fileName);
  const mimeType = nullableString(row.mimeType);
  const byteSize = nullablePositiveNumber(row.byteSize);
  const submittedAt = nullableString(row.submittedAt);
  const expiresAt = nullableString(row.expiresAt);
  if (
    source.source === "canonical"
    && (fileName === null || mimeType === null || byteSize === null)
  ) {
    invalid();
  }
  if (
    source.source === "none"
    && (
      fileName !== null
      || mimeType !== null
      || byteSize !== null
      || submittedAt !== null
      || expiresAt !== null
      || row.latestDecisionId !== null
    )
  ) {
    invalid();
  }
  return Object.freeze({
    organizationId: requireUuid(row.organizationId),
    contractorId: nullableUuid(row.contractorId),
    targetPersonUserId: nullableUuid(row.targetPersonUserId),
    personDisplayName: nullableString(row.personDisplayName),
    requirementKey: row.requirementKey,
    required: requireBoolean(row.required),
    sortOrder: requireNonNegativeInteger(row.sortOrder),
    source,
    fileName,
    mimeType,
    byteSize,
    submittedAt,
    expiresAt,
    evidenceEligible: requireBoolean(row.evidenceEligible),
    reviewState: row.reviewState,
    latestDecisionId: nullableUuid(row.latestDecisionId),
    operationallyVerified: requireBoolean(row.operationallyVerified),
    failureReason: nullableString(row.failureReason),
  });
}

export function parseCanonicalInternalComplianceReviewQueue(
  value: unknown,
): CanonicalInternalComplianceReviewQueue {
  const response = asRecord(value);
  requireExactKeys(response, ["items", "nextCursor"]);
  if (!Array.isArray(response.items)) invalid();
  return Object.freeze({
    items: Object.freeze(
      response.items.map(parseCanonicalInternalComplianceReviewQueueItem),
    ),
    nextCursor:
      response.nextCursor === null
        ? null
        : assertCanonicalInternalComplianceReviewCursor(response.nextCursor),
  });
}

export function parseCanonicalInternalComplianceReviewDetail(
  value: unknown,
): CanonicalInternalComplianceReviewDetail {
  const response = asRecord(value);
  requireExactKeys(response, [
    "organizationId",
    "contractorId",
    "targetPersonUserId",
    "requirements",
  ]);
  if (!Array.isArray(response.requirements)) invalid();
  const requirements = response.requirements.map(
    parseCanonicalInternalComplianceReviewQueueItem,
  );
  const parsedInventory = requirements.map((item) => item.requirementKey);
  const matchesKnownInventory =
    VALID_CANONICAL_INTERNAL_COMPLIANCE_REVIEW_INVENTORIES.some(
      (inventory) =>
        inventory.length === parsedInventory.length
        && inventory.every((key, index) => key === parsedInventory[index]),
    );
  if (
    !matchesKnownInventory
  ) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_requirement_inventory_invalid",
    );
  }
  const organizationId = requireUuid(response.organizationId);
  const contractorId = nullableUuid(response.contractorId);
  const targetPersonUserId = requireUuid(response.targetPersonUserId);
  if (
    requirements.some((item) =>
      item.organizationId !== organizationId
      || item.contractorId !== contractorId
      || item.targetPersonUserId !== targetPersonUserId
    )
  ) {
    invalid();
  }
  return Object.freeze({
    organizationId,
    contractorId,
    targetPersonUserId,
    requirements: Object.freeze(requirements),
  });
}

const HISTORY_ITEM_KEYS = [
  "decisionId",
  "evidenceId",
  "decision",
  "reasonCode",
  "note",
  "reviewerDisplayName",
  "reviewerRole",
  "reviewerScopeId",
  "previousDecisionId",
  "decidedAt",
] as const;

export function parseCanonicalInternalComplianceReviewHistory(
  value: unknown,
): CanonicalInternalComplianceReviewHistory {
  const response = asRecord(value);
  requireExactKeys(response, ["items"]);
  if (!Array.isArray(response.items)) invalid();
  const items = response.items.map((value) => {
    const row = asRecord(value);
    requireExactKeys(row, HISTORY_ITEM_KEYS);
    if (
      !isCanonicalInternalComplianceReviewDecision(row.decision)
      || !isCanonicalInternalComplianceReviewerRoleSnapshot(row.reviewerRole)
    ) {
      invalid();
    }
    const normalized = validateCanonicalInternalComplianceDecisionInput({
      decision: row.decision,
      reasonCode: row.reasonCode,
      note: row.note,
    });
    return Object.freeze({
      decisionId: requireUuid(row.decisionId),
      evidenceId: requireUuid(row.evidenceId),
      decision: row.decision,
      reasonCode: normalized.reasonCode,
      note: normalized.note,
      reviewerDisplayName: nullableString(row.reviewerDisplayName),
      reviewerRole: row.reviewerRole,
      reviewerScopeId: nullableUuid(row.reviewerScopeId),
      previousDecisionId: nullableUuid(row.previousDecisionId),
      decidedAt: requireString(row.decidedAt),
    });
  });
  for (let index = 1; index < items.length; index += 1) {
    if (
      items[index - 1].decidedAt > items[index].decidedAt
      || (
        items[index - 1].decidedAt === items[index].decidedAt
        && items[index - 1].decisionId > items[index].decisionId
      )
    ) {
      invalid();
    }
  }
  return Object.freeze({ items: Object.freeze(items) });
}

const DECISION_RESULT_KEYS = [
  "decisionId",
  "evidenceId",
  "decision",
  "reasonCode",
  "note",
  "reviewerRole",
  "reviewerScopeId",
  "previousDecisionId",
  "decidedAt",
  "reviewState",
  "operationallyVerified",
] as const;

export function parseCanonicalInternalComplianceReviewDecisionResult(
  value: unknown,
): CanonicalInternalComplianceReviewDecisionResult {
  const row = asRecord(value);
  requireExactKeys(row, DECISION_RESULT_KEYS);
  if (
    !isCanonicalInternalComplianceReviewDecision(row.decision)
    || row.reviewState !== row.decision
    || !isCanonicalInternalComplianceReviewerRoleSnapshot(row.reviewerRole)
  ) {
    invalid();
  }
  const normalized = validateCanonicalInternalComplianceDecisionInput({
    decision: row.decision,
    reasonCode: row.reasonCode,
    note: row.note,
  });
  return Object.freeze({
    decisionId: requireUuid(row.decisionId),
    evidenceId: requireUuid(row.evidenceId),
    decision: row.decision,
    reasonCode: normalized.reasonCode,
    note: normalized.note,
    reviewerRole: row.reviewerRole,
    reviewerScopeId: nullableUuid(row.reviewerScopeId),
    previousDecisionId: nullableUuid(row.previousDecisionId),
    decidedAt: requireString(row.decidedAt),
    reviewState: row.reviewState,
    operationallyVerified: requireBoolean(row.operationallyVerified),
  });
}

export function parseCanonicalInternalComplianceReviewPreview(
  value: unknown,
): CanonicalInternalComplianceReviewPreview {
  const row = asRecord(value);
  requireExactKeys(row, [
    "signedUrl",
    "expiresAt",
    "fileName",
    "mimeType",
    "byteSize",
  ]);
  return Object.freeze({
    signedUrl: requireString(row.signedUrl),
    expiresAt: requireString(row.expiresAt),
    fileName: requireString(row.fileName),
    mimeType: requireString(row.mimeType),
    byteSize:
      nullablePositiveNumber(row.byteSize)
      ?? invalid(),
  });
}
